import { array, assign, coerce, Describe, is, lazy, literal, number, optional, string, type, union } from 'superstruct';

import { CheckFilter, Filter } from '~modules/media-library/model/Filter';
import { makeFilterOptionId } from '~modules/media-library/model/FilterHelper';
import { FilterOption } from '~modules/media-library/model/FilterOption';

export type FilterBaseDto = {
  key: string;
  label: string;
};

export const FilterBaseDtoValidator: Describe<FilterBaseDto> = type({
  key: string(),
  label: string(),
});

export type FilterCheckboxOptionDto = {
  data: string;
  label: string;
  children: FilterCheckboxOptionDto[] | undefined;
};

export const FilterCheckboxOptionDtoValidator: Describe<FilterCheckboxOptionDto> = type({
  data: coerce(string(), number(), val => `${val}`),
  label: string(),
  children: optional(lazy(() => array(FilterCheckboxOptionDtoValidator))),
});

export type FilterCheckboxDto = FilterBaseDto & {
  type: 'checkbox';
  options: FilterCheckboxOptionDto[];
};

export const FilterCheckboxDtoValidator: Describe<FilterCheckboxDto> = assign(
  FilterBaseDtoValidator,
  type({
    type: literal('checkbox'),
    options: array(FilterCheckboxOptionDtoValidator),
  }),
);

export type FilterTextDto = FilterBaseDto & {
  type: 'text';
};

export const FilterTextDtoValidator: Describe<FilterTextDto> = assign(
  FilterBaseDtoValidator,
  type({
    type: literal('text'),
  }),
);

export const FilterDtoValidator: Describe<FilterDto> = union([FilterCheckboxDtoValidator, FilterTextDtoValidator]);

function makeCheckboxFilterOption(key: FilterCheckboxDto['key'], option: FilterCheckboxOptionDto): FilterOption {
  return {
    id: makeFilterOptionId(key, option.data),
    label: option.label,
    children:
      option.children && option.children.length > 0
        ? option.children.map(child => makeCheckboxFilterOption(key, child))
        : undefined,
  };
}

function makeCheckboxFilter(rawFilter: FilterCheckboxDto): CheckFilter {
  return {
    id: rawFilter.key,
    label: rawFilter.label,
    type: 'checkbox',
    options: (rawFilter.options || []).map(option => makeCheckboxFilterOption(rawFilter.key, option)),
  };
}

export type FilterDto = FilterCheckboxDto | FilterTextDto;

export function makeFilterList(rawFilterList: FilterDto[]): Filter[] {
  return rawFilterList
    .map(rawFilter => {
      if (is(rawFilter, FilterCheckboxDtoValidator)) {
        return makeCheckboxFilter(rawFilter);
      }
      return;
    })
    .filter(Boolean);
}

export function makeTabFilter(rawFilter: FilterDto): Filter {
  if (is(rawFilter, FilterCheckboxDtoValidator)) {
    return makeCheckboxFilter(rawFilter);
  }
  return;
}
